import React, { useState, useEffect } from "react";
import {
  DataGrid,
  GridRowModes,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { Box, Button, Select, MenuItem } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EditShareholder from "../Modal/EditShareholder";
import AssociateShareholder from "../Modal/AssociateShareholder";
import { CustomAlert } from "../../Common/Components/CustomAlert";

/* ---------------------- Custom Toolbar ---------------------- */
function CustomToolbar({ onAddRow, onAssociateShareholder }) {
  const { t, i18n } = useTranslation(["Company"]);
  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddRow}
        sx={{ textTransform: "none", mr: 1 }}
      >
        Add Shareholder
      </Button>

      <Button
        color="secondary"
        startIcon={<ManageAccountsIcon />}
        onClick={onAssociateShareholder}
        sx={{ textTransform: "none", mr: 1 }}
      >
        {t("AddExistingShareholder")}
      </Button>

      <GridToolbarQuickFilter debounceMs={500} />
    </GridToolbarContainer>
  );
}

/* -------------------- Custom Cell Editor -------------------- */
function ActiveEditCell(props) {
  const { id, field, value, api } = props;

  const handleChange = (event) => {
    api.setEditCellValue({ id, field, value: event.target.value });
  };

  return (
    <Select value={value ?? false} onChange={handleChange} fullWidth size="small">
      <MenuItem value={true}>Actif</MenuItem>
      <MenuItem value={false}>Inactif</MenuItem>
    </Select>
  );
}

/* ------------------------ Main Component ------------------------ */
function ShareholderList({ company, shareHoldersByEmplacement,refreshShareholders }) {
  const userState = useSelector((state) => state.user);
  const { t } = useTranslation(["Company"], ["Common"]);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [shareHolders, setShareHolders] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [openAddModal, setOpenAddModal] = useState(false);

  const [openAssociateShareholder, setOpenAssociateShareholder] = useState(false);

  const handleOpenAssociateShareholder = () => setOpenAssociateShareholder(true);
  const handleCloseAssociateShareholder = () => setOpenAssociateShareholder(false);

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  /* Init load */
  useEffect(() => {
    GetShareHolders();
  }, []);

  /* Build rows when data changes */
  useEffect(() => {
    BuildRows();
  }, [shareHoldersByEmplacement]);

  /* ------------ Build Rows ------------ */
  const BuildRows = (data = shareHoldersByEmplacement) => {
    if (data && data.length > 0) {
      const newRows = data.map((e, i) => ({
        id: i,
        isActive: !e.estInactif,
        firstName: e.firstname,
        lastName: e.lastName,
        username: e.username,
        email: e.email,
        NoCellulaire: e.noCellulaire,
        langue: e.language,
        department: e.department?.textEn ?? "",
        lastLogin: e.lastLogin,
        PourcentageAction: e.pourcentageAction,
      }));
      setRows(newRows);
    } else {
      setRows([]);
    }
  };

  /* -------- Save new Shareholder -------- */
  const handleSaveNewShareholder = async (newShareholder) => {
    try {
      handleCloseAddModal();
      await GetShareHolders();
      setAlertMessage("Shareholder ajouté avec succès !");
      setAlertType("success");
      setAlertOpen(true);
    } catch (err) {
      setAlertMessage("Erreur lors de l'ajout du shareholder.");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  /* ------------ Columns ------------ */
  const columns = [
    {
      field: "isActive",
      headerName: "Statut",
      width: 120,
      editable: false,
      hide: true,
      renderEditCell: (params) => <ActiveEditCell {...params} />,
      valueFormatter: (params) => (params.value ? "Actif" : "Inactif"),
    },
    { field: "firstName", headerName: "Prenom", width: 130 },
    { field: "lastName", headerName: "Nom", width: 130 },
    { field: "username", headerName: "Username", width: 130 },
    { field: "email", headerName: "Email", width: 250 },
    {
      field: "NoCellulaire",
      headerName: "No Cellulaire",
      width: 150,
      editable: true,
    },
    { field: "langue", headerName: "Langue", width: 100 },
    {
      field: "department",
      headerName: "Departement",
      width: 150,
    },
    {
      field: "PourcentageAction",
      headerName: "% Action",
      width: 100,
      editable: true,
    },
  ];

  /* -------- Edit handlers -------- */
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () =>
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    processRowUpdate(rows.find((r) => r.id === id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  /* -------- Update row -------- */
  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((r) => (r.id === newRow.id ? updatedRow : r)));
    handleSaveShareholder(updatedRow);
    return updatedRow;
  };

  /* -------- Fetch Shareholders -------- */
  const GetShareHolders = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/GetShareHolders`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      const data = await res.json();
      setShareHolders(data);
    } catch (error) {
      setAlertMessage("Error fetching shareholders");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  /* -------- Save shareholder -------- */
  const handleSaveShareholder = async (shareholder) => {
    const shareholderDTO = {
      FirstName: shareholder.firstName,
      LastName: shareholder.lastName,
      Username: shareholder.username,
      Email: shareholder.email,
      Langue: shareholder.langue,
      NoCellulaire: shareholder.NoCellulaire,
      PourcentageAction: shareholder.PourcentageAction,
      EstInactif: !shareholder.isActive,
      noCompany: company.noCompagnie,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/SaveShareholder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(shareholderDTO),
        }
      );

      const result = await response.json();

      if (result === "echec") {
        setAlertMessage("Failed to save shareholder");
        setAlertType("error");
      } else {
        setAlertMessage("Shareholder saved successfully");
        setAlertType("success");
      }
      setAlertOpen(true);

      return result;
    } catch (error) {
      setAlertMessage("Error saving shareholder");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  /* -------- Fetch address close modal -------- */
  const handleOpenDetailsShareholder = (id) => {
    const row = rows.find((r) => r.id === id);
    GetAdresse(row);
  };

  const GetAdresse = async (row) => {
    const dataShareholder = { username: row.username };
    try {
      const res = await fetch(`employe/GetAdresse`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(dataShareholder),
      });

      const data = await res.json();
      console.log("Adresse shareholder :", data);
    } catch (error) {
      setAlertMessage("Error fetching address");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  return (
    <>
      <CustomAlert
        open={alertOpen}
        message={alertMessage}
        typeMessage={alertType}
        closeMessage={() => setAlertOpen(false)}
      />

      <DataGrid
        editMode="row"
        rows={rows}
        columns={columns}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            onAddRow: handleOpenAddModal,
            onAssociateShareholder: handleOpenAssociateShareholder, // ✅ FIX
          },
        }}
        pageSizeOptions={[5, 10, 50, 100]}
        sx={{ m: 2 }}
      />

      {/* -------- Add Shareholder Modal -------- */}
      <EditShareholder
        open={openAddModal}
        handleClose={handleCloseAddModal}
        onSave={handleSaveNewShareholder}
        company={company}
        shareholders={rows}
        initialData={{}}
      />

      {/* -------- Associate Shareholder Modal -------- */}
      <AssociateShareholder
        open={openAssociateShareholder}
        handleClose={handleCloseAssociateShareholder}
        company={company}
        onAssociate={refreshShareholders}
      />
    </>
  );
}

export default ShareholderList;
